// [No changes to imports]
import React, { useState, useEffect } from 'react';
import { addDoc, collection, getDocs, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { MathfieldElement } from 'mathlive';
import 'katex/dist/katex.min.css';
import { BlockMath } from 'react-katex';
import TutorLayout from './TutorLayout';
import './AssignmentBuilder.css';
import axios from 'axios';

const QuestionBuilder = () => {
  const [subject, setSubject] = useState(localStorage.getItem('qb_subject') || '');
  const [topic, setTopic] = useState(localStorage.getItem('qb_topic') || '');
  const [subtopic, setSubtopic] = useState(localStorage.getItem('qb_subtopic') || '');
  const [clip, setClip] = useState(localStorage.getItem('qb_clip') || '');
  const [questionText, setQuestionText] = useState('');
  const [optionalQuestionText, setOptionalQuestionText] = useState('');
  const [questionLatex, setQuestionLatex] = useState('');
  const [answers, setAnswers] = useState(['']);
  const [choices, setChoices] = useState([{ text: '', isCorrect: false }]);
  const [answerPrefix, setAnswerPrefix] = useState('');
  const [answerSuffix, setAnswerSuffix] = useState('');
  const [calculator, setCalculator] = useState(localStorage.getItem('qb_calculator') || 'yes');
  const [difficulty, setDifficulty] = useState(localStorage.getItem('qb_difficulty') || '');
  const [grade, setGrade] = useState(localStorage.getItem('qb_grade') || '');
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [filter, setFilter] = useState({
    subject: '',
    topic: '',
    subtopic: '',
    clip: ''
  });  
  const [editMode, setEditMode] = useState(false);
  const [currentEditingId, setCurrentEditingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [questionFormat, setQuestionFormat] = useState('latex'); // default


  useEffect(() => {
    fetchQuestions();
  }, []);
  


  useEffect(() => {
    if (!customElements.get('math-field')) {
      customElements.define('math-field', MathfieldElement);
    }
    const field = document.getElementById('question-latex-field');
    if (field) {
      field.value = questionLatex;
      field.addEventListener('input', (e) => setQuestionLatex(e.target.value));
    }
  }, [questionLatex]);

  useEffect(() => localStorage.setItem('qb_subject', subject), [subject]);
  useEffect(() => localStorage.setItem('qb_topic', topic), [topic]);
  useEffect(() => localStorage.setItem('qb_subtopic', subtopic), [subtopic]);
  useEffect(() => localStorage.setItem('qb_clip', clip), [clip]);
  useEffect(() => localStorage.setItem('qb_calculator', calculator), [calculator]);
  useEffect(() => localStorage.setItem('qb_difficulty', difficulty), [difficulty]);
  useEffect(() => localStorage.setItem('qb_grade', grade), [grade]);

  const fetchQuestions = async () => {
    const querySnapshot = await getDocs(collection(db, 'questions'));
    const questionsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setSavedQuestions(questionsData);
  };

  const saveQuestion = async () => {
    const validAnswers = answers.map(a => a.trim()).filter(Boolean);
    const validChoices = choices.filter(c => c.text.trim());
console.log('🔍 Validation check:', {
  subject,
  topic,
  subtopic,
  clip,
  questionFormat,
  questionLatex,
  questionText,
  validAnswers
});

 const isEmptyQuestion =
  questionFormat === 'latex'
    ? !questionLatex.trim()
    : !questionText.trim();

if (isEmptyQuestion && validAnswers.length === 0 && validChoices.length === 0) {
  return alert('Please provide at least a question or an answer or a choice.');
}

    setEditMode(false);
    setCurrentEditingId(null);
    fetchQuestions();
    setSuccessMessage('Question Saved Successfully ✅');
    setTimeout(() => setSuccessMessage(''), 3000);
    
    const data = {
      subject,
      topic,
      subtopic,
      clip,
      questionText: questionText.trim(), // Always include this
      optionalQuestionText: optionalQuestionText.trim(), // Always include this
      answers: validAnswers,
      answerPrefix,
      answerSuffix,
      calculator,
      difficulty,
      questionFormat,
      grade
    };
    
    // ✅ Set LaTeX and clear plain text if LaTeX is selected
    if (questionFormat === 'latex') {
  const mathField = document.getElementById('question-latex-field');
  const currentLatex = mathField?.value?.trim() || '';
  data.questionLatex = currentLatex;
  data.questionText = questionText.trim(); // Optional prefix text
} else {
  data.questionText = questionText.trim();
  data.questionLatex = '';
}

    
    
    

    if (validChoices.length > 0) {
      data.choices = validChoices;
    }

    if (editMode && currentEditingId) {
      await setDoc(doc(db, 'questions', currentEditingId), data);
    } else {
      await addDoc(collection(db, 'questions'), data);
    }

   
    fetchQuestions();
  };

  const resetForm = () => {
    setQuestionText('');
    setOptionalQuestionText('');
    setQuestionLatex('');
    setAnswers(['']);
    setChoices([{ text: '', isCorrect: false }]);
    setAnswerPrefix('');
    setAnswerSuffix('');
    setEditMode(false);
    setCurrentEditingId(null);
  };

  const deleteQuestion = async (id) => {
    await deleteDoc(doc(db, 'questions', id));
    fetchQuestions();
  };

  const generateAIQuestion = async () => {
    if (!subject || !topic || !subtopic || !clip || !difficulty) {
      alert("Please fill out subject, topic, subtopic, clip, and difficulty first.");
      return;
    }
  
    try {
      const response = await axios.post(
        'http://localhost:5001/generate-ai-question',
        {
          subject,
          topic,
          subtopic,
          clip,
          difficulty,
        }
      );
  
      const aiContent = JSON.parse(response.data);
  
      setQuestionText(aiContent.questionText || '');
      setQuestionLatex(aiContent.questionLatex || '');
      setAnswers(aiContent.answers || ['']);
      setChoices(
        (aiContent.choices || []).map((choice, index) => ({
          text: choice,
          isCorrect: index === 0,
        }))
      );
  
      setOptionalQuestionText('');
      setAnswerPrefix('');
      setAnswerSuffix('');
  
      alert('✅ AI question generated!');
    } catch (error) {
      console.error("❌ Error generating AI question:", error);
      alert('Failed to generate AI question. Check console for errors.');
    }
  };
  
  

  const updateAnswer = (index, value) => {
    const updated = [...answers];
    updated[index] = value;
    setAnswers(updated);
  };

  const updateChoiceText = (index, value) => {
    const updated = [...choices];
    updated[index] = { ...updated[index], text: value };
    setChoices(updated);
  };

  const markCorrectChoice = (index) => {
    const updated = choices.map((c, i) => ({ ...c, isCorrect: i === index }));
    setChoices(updated);
  };

  const addAnswerField = () => setAnswers([...answers, '']);
  const addChoiceField = () => setChoices([...choices, { text: '', isCorrect: false }]);

  const loadQuestionForEditing = (q) => {
    setSubject(q.subject || '');
    setTopic(q.topic || '');
    setSubtopic(q.subtopic || '');
    setClip(q.clip || '');
    setQuestionText(q.questionText || '');
    setOptionalQuestionText(q.optionalQuestionText || '');
    setQuestionLatex(q.questionLatex || '');
    setQuestionFormat(q.questionFormat || 'latex');
    setAnswers(q.answers || ['']);

    if (Array.isArray(q.choices)) {
      const normalizedChoices = q.choices.map(choice =>
        typeof choice === 'string' ? { text: choice, isCorrect: false } : choice
      );
      setChoices(normalizedChoices);
    } else {
      setChoices([{ text: '', isCorrect: false }]);
    }

    setAnswerPrefix(q.answerPrefix || '');
    setAnswerSuffix(q.answerSuffix || '');
    setCalculator(q.calculator || 'yes');
    setDifficulty(q.difficulty || '');
    setGrade(q.grade || '');
    setEditMode(true);
    setCurrentEditingId(q.id);

    setTimeout(() => {
      const field = document.getElementById('question-latex-field');
      if (field) field.value = q.questionLatex || '';
    }, 50);
  };

  const filteredQuestions = savedQuestions.filter(q =>
    (!filter.subject || q.subject === filter.subject) &&
    (!filter.topic || q.topic === filter.topic) &&
    (!filter.subtopic || q.subtopic === filter.subtopic) &&
    (!filter.clip || q.clip === filter.clip)
  );
  

  const getUnique = (field) => [...new Set(savedQuestions.map(q => q[field]).filter(Boolean))];

  const showExtraFields = ['gcse maths', 'a-level maths', 'gcse french', 'a-level french'].includes(subject.trim().toLowerCase());
  const getFilteredTopics = () => {
    return [
      ...new Set(
        savedQuestions
          .filter(q => !filter.subject || q.subject === filter.subject)
          .map(q => q.topic)
          .filter(Boolean)
      )
    ];
  };
  
  const getFilteredSubtopics = () => {
    return [
      ...new Set(
        savedQuestions
          .filter(q =>
            (!filter.subject || q.subject === filter.subject) &&
            (!filter.topic || q.topic === filter.topic)
          )
          .map(q => q.subtopic)
          .filter(Boolean)
      )
    ];
  };
  
  const getFilteredClips = () => {
    return [
      ...new Set(
        savedQuestions
          .filter(q =>
            (!filter.subject || q.subject === filter.subject) &&
            (!filter.topic || q.topic === filter.topic) &&
            (!filter.subtopic || q.subtopic === filter.subtopic)
          )
          .map(q => q.clip)
          .filter(Boolean)
      )
    ];
  };
  
  return (
    <TutorLayout>
      <div className="builder-wrapper">
        <h1>📘 Question Builder</h1>
        <button onClick={generateAIQuestion} className="generate-btn" style={{ marginBottom: '1rem' }}>
  🤖 Generate AI Question
</button>


        <div className="section">
          <label>Subject:</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. GCSE Maths" className="fancy-dropdown" />

          {showExtraFields && (
            <>
              <label>Topic:</label>
              <input value={topic} onChange={(e) => setTopic(e.target.value)} className="fancy-dropdown" placeholder="e.g. Number" />

              <label>Subtopic:</label>
              <input value={subtopic} onChange={(e) => setSubtopic(e.target.value)} className="fancy-dropdown" placeholder="e.g. Fractions" />

              <label>Clip:</label>
              <input value={clip} onChange={(e) => setClip(e.target.value)} className="fancy-dropdown" placeholder="e.g. Add and Subtract Fractions" />

              <label>Calculator Allowed?</label>
              <select value={calculator} onChange={(e) => setCalculator(e.target.value)} className="fancy-dropdown">
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>

              <label>Difficulty (1-10):</label>
              <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="fancy-dropdown">
                <option value="">Select Difficulty</option>
                {[...Array(10)].map((_, i) => (
                  <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                ))}
              </select>

              {subject.trim().toLowerCase() === 'a-level maths' ? (
  <>
    <label>Year:</label>
    <select value={grade} onChange={(e) => setGrade(e.target.value)} className="fancy-dropdown">
      <option value="">Select Year</option>
      <option value="AS Level">AS Level</option>
      <option value="A-Level">A-Level</option>
    </select>
  </>
) : (
  <>
    <label>Grade (1–9):</label>
    <select value={grade} onChange={(e) => setGrade(e.target.value)} className="fancy-dropdown">
      <option value="">Select Grade</option>
      {[...Array(9)].map((_, i) => (
        <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
      ))}
    </select>
  </>
)}

            </>
          )}

          <label>Question Text:</label>
          <input value={questionText} onChange={(e) => setQuestionText(e.target.value)} className="fancy-dropdown" placeholder="e.g. Solve the equation" />
          <select value={questionFormat} onChange={(e) => setQuestionFormat(e.target.value)} className="fancy-dropdown">
  <option value="latex">LaTeX</option>
  <option value="text">Plain Text</option>
</select>
          <label>LaTeX Expression:</label>
          {questionFormat === 'latex' ? (
  <math-field id="question-latex-field" style={{ width: '100%', marginBottom: '1rem' }} />
) : (
  <textarea
    value={questionText}
    onChange={(e) => setQuestionText(e.target.value)}
    rows={4}
    placeholder="Type your question here using plain text"
    style={{ width: '100%', marginBottom: '1rem' }}
  />
)}


          <label>Optional Question Text:</label>
          <textarea value={optionalQuestionText} onChange={(e) => setOptionalQuestionText(e.target.value)} className="fancy-dropdown" placeholder="e.g. Give your answer in its simplest form." rows={3} />

          <label>Answer Prefix:</label>
          <input value={answerPrefix} onChange={(e) => setAnswerPrefix(e.target.value)} className="fancy-dropdown" placeholder="e.g. x =" />

          <label>Answer Suffix:</label>
          <input value={answerSuffix} onChange={(e) => setAnswerSuffix(e.target.value)} className="fancy-dropdown" placeholder="e.g. cm" />

          <label>Acceptable Answers:</label>
          {answers.map((ans, i) => (
            <math-field
              key={i}
              value={ans}
              onInput={(e) => updateAnswer(i, e.target.value)}
              style={{ width: '100%', marginBottom: '0.75rem' }}
            />
          ))}
          <button onClick={addAnswerField} className="add-btn">➕ Add Answer</button>

          <label>Multiple Choice Options:</label>
          {choices.map((choice, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <math-field
                style={{ width: '100%', fontSize: '1rem' }}
                value={choice.text}
                onInput={(e) => updateChoiceText(i, e.target.value)}
              />
              <label>
                <input
                  type="radio"
                  name="correctChoice"
                  checked={choice.isCorrect}
                  onChange={() => markCorrectChoice(i)}
                /> Correct
              </label>
            </div>
          ))}
          <button onClick={addChoiceField} className="add-btn">➕ Add Choice</button>

          <button onClick={saveQuestion} className="save-btn" style={{ marginTop: '1rem' }}>
            {editMode ? '✅ Update Question' : '💾 Save Question'}
          </button>
          {successMessage && (
  <div style={{ color: 'green', marginBottom: '1rem', fontWeight: 'bold' }}>
    {successMessage}
  </div>
)}
        </div>

        <div className="section">
        <h3>🔍 Filter Questions</h3>

<select
  onChange={(e) => setFilter({ subject: e.target.value, topic: '', subtopic: '', clip: '' })}
  value={filter.subject}
  className="fancy-dropdown"
>
  <option value="">All Subjects</option>
  {getUnique('subject').map(s => <option key={s}>{s}</option>)}
</select>

<select
  onChange={(e) => setFilter({ ...filter, topic: e.target.value, subtopic: '', clip: '' })}
  value={filter.topic}
  className="fancy-dropdown"
>
  <option value="">All Topics</option>
  {getFilteredTopics().map(t => <option key={t}>{t}</option>)}
</select>

<select
  onChange={(e) => setFilter({ ...filter, subtopic: e.target.value, clip: '' })}
  value={filter.subtopic}
  className="fancy-dropdown"
>
  <option value="">All Subtopics</option>
  {getFilteredSubtopics().map(st => <option key={st}>{st}</option>)}
</select>

<select
  onChange={(e) => setFilter({ ...filter, clip: e.target.value })}
  value={filter.clip}
  className="fancy-dropdown"
>
  <option value="">All Clips</option>
  {getFilteredClips().map(c => <option key={c}>{c}</option>)}
</select>



        </div>

        <div className="section">
  <h3>📚 Saved Questions</h3>

  {!filter.clip ? (
    <p>🎯 Select a clip above to view saved questions.</p>
  ) : filteredQuestions.length === 0 ? (
    <p>No questions found for this clip.</p>
  ) : (
    filteredQuestions.map((q) => {
      const hasContent =
      (q.questionText && q.questionText.trim() !== '') ||
      (q.questionLatex && q.questionLatex.trim() !== '') ||
      (q.optionalQuestionText && q.optionalQuestionText.trim() !== '') ||
      (Array.isArray(q.questionParagraphs) && q.questionParagraphs.length > 0) ||
      (Array.isArray(q.choices) && q.choices.length > 0);
    
    
      if (!hasContent) return null; // 🔒 Skip blank questions
    
      return (
        <div key={q.id} className="question-card">
                <div className="question-actions-top">
  <button onClick={() => loadQuestionForEditing(q)} className="edit-btn">✏️</button>
  <button onClick={() => deleteQuestion(q.id)} className="delete-btn">🗑</button>
</div>

              {q.questionParagraphs?.length > 0 && (
  <div style={{ display: "flex", flexDirection: "column", gap: "1rem", marginBottom: "1rem" }}>
    {q.questionParagraphs.map((para, i) => (
      <div key={i}>
        {para.image && (
          <img
          src={para.image}
          alt={`Image ${i + 1}`}
          style={{
            width: "150px",           // ✅ Force image width
            height: "150px",          // ✅ Force image height
            objectFit: "contain",     // ✅ Keep aspect ratio clean
            display: "block",
            margin: "0 auto 1rem",
          }}
        />
        
        )}
        {para.text && <p style={{ marginTop: "0.5rem", lineHeight: "1.5" }}>{para.text}</p>}
      </div>
    ))}
  </div>
)}

            
              
<div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1rem" }}>
{q.questionText && (
  <div style={{ whiteSpace: 'pre-wrap', paddingLeft: "1rem", lineHeight: "1.6" }}>
    <strong>Q:</strong> {q.questionText}
  </div>
)}


{q.questionFormat === 'latex' && q.questionLatex && (
  <div style={{ paddingLeft: "1rem" }}>
    <BlockMath math={q.questionLatex} />
  </div>
)}

{q.questionFormat === 'text' && q.questionLatex && (
  <div style={{ whiteSpace: 'pre-wrap', paddingLeft: "1rem", lineHeight: '1.6' }}>
    {q.questionLatex}
  </div>
)}


  {q.optionalQuestionText && (
    <div style={{ paddingLeft: "1rem", color: "#333" }}>
      {q.optionalQuestionText}
    </div>
  )}
</div>

            
              {q.choices?.length > 0 && (
                <div className="choice-grid">
                {q.choices.map((c, i) => (
                  <div key={i} className="choice-box">
                    <BlockMath math={c.text} /> {c.isCorrect && <strong>✅</strong>}
                  </div>
                ))}
              </div>
              
              )}
            
              
            </div>
            
          );
        })
          )}
        </div>
      </div>
    </TutorLayout>
  );
};

export default QuestionBuilder;